import React from 'react';
import { useNavigate } from 'react-router-dom';

export function PrivacyPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white/90 font-sans px-6 py-12">
      <div className="max-w-3xl mx-auto">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
          className="text-sm text-[#3b82f6] hover:text-[#3b82f6]/80 transition-colors"
        >
          &larr; Back
        </button>

        <h1 className="text-3xl font-bold mt-6 mb-2 text-white">
          FlashTools Connect - Privacy Policy
        </h1>
        <p className="text-white/50 mb-10">Last updated: January 2025</p>

        <p className="mb-8 text-white/70">
          FlashTools Connect is a Chrome extension that helps users connect their ticketing account
          to FlashTools for listing management.
        </p>

        <Section title="What We Collect">
          <p className="mb-3">
            When you log into your ticketing account while the extension is active, we collect:
          </p>
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li>
              Session credentials (session token and session JWT) to authenticate API requests on
              your behalf
            </li>
            <li>User ID associated with your ticketing account</li>
          </ul>
        </Section>

        <Section title="How We Use It">
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li>
              Session credentials are used solely to connect your ticketing account to FlashTools so
              you can manage your listings
            </li>
            <li>
              User ID is used to display your connection status in the extension popup
            </li>
          </ul>
        </Section>

        <Section title="How We Store It">
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li>
              Session credentials are held in memory only and are never written to disk or persistent
              storage
            </li>
            <li>
              Only non-sensitive metadata (user ID, connection timestamp) is saved to the
              extension's local storage for popup display
            </li>
            <li>All captured data automatically expires and is deleted after 10 minutes</li>
          </ul>
        </Section>

        <Section title="What We Do NOT Do">
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li>We do not sell or share your data with third parties</li>
            <li>We do not track your browsing history or activity on any website</li>
            <li>
              We do not collect personal information such as your name, email, or phone number
            </li>
            <li>We do not run in the background or collect data passively</li>
            <li>We do not use your data for advertising, analytics, or profiling</li>
          </ul>
        </Section>

        <Section title="Data Transmission">
          <p className="text-white/70">
            Session credentials are transmitted only between the extension and the FlashTools web
            application (flashtools.fm) over HTTPS. No data is sent to any other server or service.
          </p>
        </Section>

        <Section title="Data Deletion">
          <p className="text-white/70">
            You can clear all captured data at any time by clicking the "Clear Data" button in the
            extension popup. Data also expires automatically after 10 minutes.
          </p>
        </Section>

        <Section title="Permissions">
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li>
              <strong className="text-white/90">storage:</strong> Save connection status metadata
              for the extension popup
            </li>
            <li>
              <strong className="text-white/90">tabs:</strong> Switch you back to the FlashTools tab
              after login
            </li>
            <li>
              <strong className="text-white/90">Host access (crowdvolt.com):</strong> Detect
              successful login and capture session credentials
            </li>
            <li>
              <strong className="text-white/90">Host access (flashtools.fm):</strong> Deliver
              credentials to the FlashTools web app
            </li>
          </ul>
        </Section>

        <Section title="Changes to This Policy">
          <p className="text-white/70">
            We may update this policy from time to time. Any changes will be reflected on this page
            with an updated date.
          </p>
        </Section>

        <Section title="Contact">
          <p className="text-white/70">
            If you have questions about this privacy policy, contact us at{' '}
            <a
              href="mailto:support@flashtickets.fm"
              className="text-[#3b82f6] hover:underline"
            >
              support@flashtickets.fm
            </a>
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-semibold text-white mb-3">{title}</h2>
      {children}
    </section>
  );
}
